import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Avatar } from '@picsart/design-system/components/Avatar';
import { BaseBottomSheet } from '@picsart/design-system/components/BaseBottomSheet';
import {
  InlineButton,
  ControlSizesMD,
} from '@picsart/design-system/components/InlineButton';
import { IconCross } from '@picsart/design-system/foundation/icons/IconCross';
import { BadgeTypes } from '@picsart/design-system/components/Badge';
import { StackingContext } from '@picsart/design-system/foundation/stackingContext';
import { useClickOutsideRef } from '@picsart/design-system/components/hooks';
import { getContext } from '@/context';
import { MINIAPP_PACKAGE_ID } from '@/consts/configs';
import { fetchUserCredits } from '@/requests/user/userProfile';
import { logout } from '@/utils/user/authActions';
import { publishSdkEvent } from '@/utils/user/publishSdkEvent';
import { useDeviceViewport } from '@/hooks/useViewport';
import { UserMenuContent } from './UserMenuContent';
import useStyles from './styles';
import { SHEET_SNAP_AUTHORIZED, SHEET_SNAP_GUEST } from '@/consts/userMenu';

const tierToBadge = (
  tierId: string | null | undefined
): BadgeTypes | undefined => {
  if (!tierId) return undefined;
  const id = tierId.toLowerCase();
  if (id.includes('ultra')) return BadgeTypes.Ultra;
  if (id.includes('max')) return BadgeTypes.Max;
  if (id.includes('pro')) return BadgeTypes.Pro;
  if (id.includes('plus')) return BadgeTypes.Plus;
  return undefined;
};

interface UserMenuButtonProps {
  isAuthorized: boolean;
  username: string;
  avatarSrc?: string;
  tierId?: string | null;
  showUserMenu: boolean;
  onCloseApp: () => void;
  onSettingsClick?: () => void;
}

export const UserMenuButton = ({
  isAuthorized,
  username,
  avatarSrc,
  tierId,
  showUserMenu,
  onCloseApp,
  onSettingsClick,
}: UserMenuButtonProps) => {
  const classes = useStyles();
  const { isMobile } = useDeviceViewport();
  const [menuOpen, setMenuOpen] = useState(false);
  const [credits, setCredits] = useState<number | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const dropdownRef = useClickOutsideRef(closeMenu, [buttonRef], false);

  const snapPoints = useMemo(
    () => (isAuthorized ? SHEET_SNAP_AUTHORIZED : SHEET_SNAP_GUEST),
    [isAuthorized]
  );

  useEffect(() => {
    if (!menuOpen || !isAuthorized) return;
    let cancelled = false;
    fetchUserCredits().then((res) => {
      if (cancelled) return;
      if (res) setCredits((res.credits ?? 0) + (res.addonCredits ?? 0));
    });
    return () => {
      cancelled = true;
    };
  }, [menuOpen, isAuthorized]);

  const handleLogout = useCallback(async () => {
    setMenuOpen(false);
    await logout();
  }, []);

  const handleOpenCreditsScreen = useCallback(async () => {
    try {
      const result = await getContext().handlers.publishEvent({
        event: 'OpenOfferScreen',
        data: {
          source: MINIAPP_PACKAGE_ID,
          action: MINIAPP_PACKAGE_ID,
          touchpoint: MINIAPP_PACKAGE_ID,
          is_pro_touchpoint: true,
          toolId: 'genai-studio-v2',
        },
      });
      if (result) {
        const res = await fetchUserCredits();
        if (res) setCredits((res.credits ?? 0) + (res.addonCredits ?? 0));
      }
    } catch {
      console.error('[UserMenuButton] Error opening offer screen');
    }
  }, []);

  const handleOpenRegistration = useCallback(() => {
    setMenuOpen(false);
    publishSdkEvent({
      event: 'OpenRegistrationScreen',
      data: {
        source: MINIAPP_PACKAGE_ID,
        action: MINIAPP_PACKAGE_ID,
      },
    });
  }, []);

  const handleSettingsClick = useCallback(() => {
    setMenuOpen(false);
    onSettingsClick?.();
  }, [onSettingsClick]);

  if (!showUserMenu) {
    return (
      <InlineButton
        dataTestId="genai-user-menu-close"
        data-testid="genai-user-menu-close"
        size={ControlSizesMD}
        centerIcon={IconCross}
        onClick={onCloseApp}
        aria-label="Close"
        isWithoutBorder
        isWithoutBackground
      />
    );
  }

  const avatarBadge = tierToBadge(tierId);

  const content = (
    <UserMenuContent
      isAuthorized={isAuthorized}
      username={username}
      avatarSrc={avatarSrc}
      avatarBadge={avatarBadge}
      credits={credits}
      onCreditsClick={handleOpenCreditsScreen}
      onSettingsClick={handleSettingsClick}
      onLogout={handleLogout}
      onSignIn={handleOpenRegistration}
    />
  );

  return (
    <div className={classes.wrapper}>
      <button
        ref={buttonRef}
        type="button"
        className={classes.avatarBtn}
        onClick={() => setMenuOpen((v) => !v)}
        aria-label="User menu"
      >
        <Avatar
          size={32}
          imageSrc={isAuthorized ? avatarSrc : undefined}
          badge={isAuthorized ? avatarBadge : undefined}
        />
      </button>

      {isMobile ? (
        <BaseBottomSheet
          isOpen={menuOpen}
          onClose={closeMenu}
          snapPoints={snapPoints}
          defaultActiveIndex={0}
          ariaLabel="User menu"
          contentClassName={classes.sheetContent}
          zIndex={StackingContext.PopoverLayer}
        >
          {content}
        </BaseBottomSheet>
      ) : (
        menuOpen && (
          <div ref={dropdownRef} className={classes.dropdown}>
            {content}
          </div>
        )
      )}
    </div>
  );
};
